<template>
  <DeckCaptionsContainer
    :show="show"
    :caption="caption"
    :transition-stage="transitionStage"
    :class="[{ 'no-transition': noTransition }, transitionStage]"
    :hide-on-mobile="isSidebarOpen"
    data-testid="slide-caption"
  >
    <div>
      <button
        class="appearance-none bg-transparent absolute z-10 -cursor-pointer top-2 right-1 p-0"
        data-testid="slide-caption-close-btn"
        :disabled="transitionStage !== 'transitioned'"
        :class="[transitionStage !== 'transitioned' ? 'cursor-not-allowed' : 'cursor-pointer']"
        @click="expireCaption"
      >
        <XIcon v-show="show && transitionStage !== 'expired'" class="h-6 w-6 text-white m-2" />
      </button>
      <div
        class="captions-text-container"
        :class="[show && transitionStage !== 'expired' ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none']"
      >
        <div class="captions-text whitespace-pre-line next">
          {{ caption }}
          <div v-if="show && isCaptionOverflowing()" class="h-4 w-full bg-transparent" />
        </div>
        <div class="captions-text whitespace-pre-line prev absolute top-0 left-0">{{ prevCaption }}</div>
      </div>
    </div>
  </DeckCaptionsContainer>
</template>

<script lang="ts" setup>
import XIcon from '@/components/icons/x.svg'
import type { Slide } from '@/entities/deck'
import { nextTick, type DeepReadonly } from 'vue'

const props = defineProps({
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    default: null,
  },
  show: {
    type: Boolean,
    default: true,
  },
  noTransition: {
    type: Boolean,
    default: false,
  },
  isSidebarOpen: {
    type: Boolean,
    default: false,
  },
  slideRefreshKey: {
    type: Number,
    default: 0,
  },
})
const tokens: Array<() => void> = []
const cancelled = Symbol()
const timer = (durationInSeconds: number) =>
  new Promise(resolve => {
    const timer = setTimeout(resolve, durationInSeconds * 1000)
    tokens.push(() => {
      clearTimeout(timer)
      resolve(cancelled)
    })
  })
const cancelAll = () => {
  tokens.forEach(cancel => cancel())
  tokens.splice(0)
}

const { show, slide } = toRefs(props)

const prevCaption = ref<string | null | undefined>()
const caption = ref(props.slide?.description ?? undefined)
const transitionStage = ref<'will-transition' | 'transitioning' | 'transitioned' | 'expired'>('transitioned')
const captionTextContainerRef: Ref<HTMLElement | null> = ref(null)

// needed to apply space at the bottom of scrollable text
const isCaptionOverflowing = () => {
  if (!captionTextContainerRef.value) {
    captionTextContainerRef.value = document.querySelector('.captions-text-container')
    return
  }

  return captionTextContainerRef.value.scrollHeight > captionTextContainerRef.value.clientHeight
}

watch(
  [slide, show, toRef(props, 'slideRefreshKey')],
  async ([slide, show], [prevSlide]) => {
    if (!show) return

    cancelAll()

    // outgoing: visible, no transition
    // incoming: not in DOM
    if (transitionStage.value !== 'expired') {
      transitionStage.value = 'transitioned'
    } else {
      // if expired but still transitioning, finish the process
      caption.value = undefined
    }
    await nextTick()

    // outgoing: visible, no transition
    // incoming: hidden, no transition
    transitionStage.value = 'will-transition'
    if (slide === prevSlide) {
      // transition from no caption to caption showing
      prevCaption.value = undefined
    } else {
      // transition from caption to caption
      prevCaption.value = prevSlide?.description
    }
    caption.value = slide?.description ?? undefined
    if ((await timer(0)) === cancelled) return

    // outgoing: hidden, 1s transition
    // incoming: visible, 1s transition
    transitionStage.value = 'transitioning'
    if ((await timer(1)) === cancelled) return

    // outgoing: hidden, no transition
    // incoming: visible, no transition
    transitionStage.value = 'transitioned'
  },
  { immediate: true }
)

const expireCaption = async () => {
  transitionStage.value = 'expired'

  if ((await timer(1)) === cancelled) return cancelled
  caption.value = undefined
}
</script>

<style lang="stylus" scoped>
.captions
  .captions-text-container
    @apply p-0 pr-6 bg-opacity-0 /* CaptionContainer already has transparency */
  &.expired
    @apply delay-100 transition-all duration-300 p-0

  .captions-text
    &.prev
      @apply h-inherit overflow-hidden h-0

    &:empty
      @apply hidden

  &.show.will-transition
    .prev
      @apply opacity-100 transition-none h-0 p-4 pr-10
    .next
      @apply opacity-0 transition-none
  &.show.transitioning
    .prev
      @apply opacity-0 transition-all duration-500 ease-in-out h-0 p-4 pr-10
    .next
      @apply opacity-100 transition-all duration-500 ease-in-out h-full

  &.show.transitioned
    .prev
      @apply opacity-0 transition-none
    .next
      @apply opacity-100 transition-none
  &.show.expired
    .prev
      @apply opacity-0 transition-none
    .next
      @apply opacity-0 transition-all duration-500 ease-in-out

// transition for the close button and its enclosing container
.fade-enter-active, .fade-leave-active
  @apply transition-all duration-500

  button
    @apply transition-all duration-500

.fade-enter, .fade-leave-to
  @apply opacity-0

  button
    @apply opacity-0
</style>
